import Icon from '../common/Icon.jsx';

/**
 * ChatBubble — renders either a user message or a rich assistant message.
 *
 * Assistant messages are composed of typed `blocks` (paragraph, heading,
 * list, data, quote, callout). This deliberate schema keeps the renderer
 * decoupled from whatever upstream produces them (mock catalogue today,
 * LLM stream tomorrow).
 *
 * Visual rules:
 *   - User bubbles sit on the right with an F1-red gradient
 *   - Assistant bubbles sit on the left with a carbon-grey body and an
 *     "F1" avatar
 *   - Data blocks use tabular-nums + JetBrains Mono so columns align
 *   - Callouts get a subtle red rail to draw the eye to actionable hints
 */
export default function ChatBubble({ message }) {
  if (message.role === 'user') return <UserBubble message={message} />;
  return <AssistantBubble message={message} />;
}

function UserBubble({ message }) {
  return (
    <div className="mb-3 flex justify-end animate-slide-up">
      <div className="max-w-[82%] rounded-2xl rounded-br-md bg-gradient-to-br from-f1-red to-f1-redDeep px-4 py-2.5 text-sm leading-relaxed text-white shadow-glow-red-soft">
        {message.text}
      </div>
    </div>
  );
}

function AssistantBubble({ message }) {
  return (
    <div className="mb-3 flex items-end gap-2 animate-slide-up">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-f1-red to-f1-redDeep shadow-glow-red-soft">
        <span className="text-xs font-bold text-white">F1</span>
      </div>
      <div className="max-w-[88%] flex-1 rounded-2xl rounded-bl-md bg-bg-card px-4 py-3 ring-1 ring-stroke shadow-card">
        {message.blocks.map((block, idx) => (
          <BlockRenderer key={idx} block={block} isFirst={idx === 0} />
        ))}
      </div>
    </div>
  );
}

/**
 * BlockRenderer — pure dispatch on block.type. Each branch is a small leaf
 * component to make the renderer trivially testable and easy to extend.
 * Add new block types here AND in the response catalogue type comments.
 */
function BlockRenderer({ block, isFirst }) {
  const spacing = isFirst ? '' : 'mt-3';
  switch (block.type) {
    case 'paragraph':
      return (
        <p className={`${spacing} text-sm leading-relaxed text-ink-primary`}>{block.text}</p>
      );

    case 'heading':
      return (
        <h4
          className={`${spacing} text-2xs font-bold uppercase tracking-widest-plus text-f1-red`}
        >
          {block.text}
        </h4>
      );

    case 'list':
      return (
        <ul className={`${spacing} space-y-1.5`}>
          {block.items.map((item, i) => (
            <li
              key={i}
              className="relative pl-4 text-sm leading-relaxed text-ink-secondary before:absolute before:left-0 before:top-2 before:h-1 before:w-1 before:rounded-full before:bg-f1-red"
            >
              {item}
            </li>
          ))}
        </ul>
      );

    case 'data':
      return (
        <div className={`${spacing} overflow-hidden rounded-xl bg-bg-card2 ring-1 ring-stroke`}>
          {block.label ? (
            <div className="border-b border-stroke px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest-plus text-ink-tertiary">
              {block.label}
            </div>
          ) : null}
          <div className="divide-y divide-stroke/60">
            {block.rows.map((row, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
              >
                <span className="truncate text-ink-secondary">{row.k}</span>
                <span className="font-mono tabular-nums text-ink-primary">{row.v}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case 'quote':
      return (
        <blockquote
          className={`${spacing} border-l-2 border-f1-red pl-3 italic text-sm leading-relaxed text-ink-secondary`}
        >
          “{block.text}”
          {block.attribution ? (
            <footer className="mt-1.5 text-2xs uppercase tracking-widest-plus not-italic text-ink-tertiary">
              — {block.attribution}
            </footer>
          ) : null}
        </blockquote>
      );

    case 'callout':
      return (
        <div
          className={`${spacing} flex items-start gap-2 rounded-lg bg-f1-red/10 px-3 py-2 ring-1 ring-f1-red/30`}
        >
          <Icon name="bolt" size={14} className="mt-0.5 shrink-0 text-f1-red" />
          <p className="text-xs leading-relaxed text-ink-primary">{block.text}</p>
        </div>
      );

    default:
      if (typeof console !== 'undefined') {
        console.warn('ChatBubble: unknown block type', block.type);
      }
      return null;
  }
}
